import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from 'context/auth';
import {
  Box,
  Heading,
  useToast,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Center,
  Tag,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  ListItem,
  UnorderedList,
  IconButton,
  Link,
  TagLabel,
  TagLeftIcon,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  Button,
  Flex,
  Spacer,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Icon,
  useBoolean,
} from '@chakra-ui/react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { MCFFormsContext } from 'context/mcfforms';
import { CgNotes } from 'react-icons/cg';
import { BsCheckCircle } from 'react-icons/bs';
import { TbEyeCheck } from 'react-icons/tb';
import {
  CheckIcon,
  ChevronDownIcon,
  DeleteIcon,
  EditIcon,
  RepeatIcon,
  SearchIcon,
  TimeIcon,
  WarningTwoIcon,
} from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';

export default function ManageSubmittedMCFS(props) {
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const { MCFForms, getMCFForms, myPartCount } = useContext(MCFFormsContext);
  const [isLoading, setIsLoading] = useState(false);
  const [accordionIndex, setAccordionIndex] = useState([]);
  const copyDebouncer = useRef(false);

  useEffect(() => {
    if (MCFForms) {
      let index = [];
      if (myPartCount.current.pendingMentorReview > 0) {
        index.push(0);
      }
      if (myPartCount.current.pendingReview > 0) {
        index.push(1);
      }
      if (myPartCount.current.pendingFab > 0) {
        index.push(2);
      }
      setAccordionIndex(index);
    }
  }, [MCFForms]);

  const handleFilePath = rowData => {
    const handleOnClick = e => {
      if (!copyDebouncer.current) {
        navigator.clipboard.writeText(rowData.filePath);
        toast({
          title: 'File Path copied to clipboard',
          description: rowData.filePath,
          duration: 2000,
          isClosable: true,
        });
        copyDebouncer.current = true;
        setTimeout(() => {
          copyDebouncer.current = false;
        }, 500);
      }
    };
    return (
      <Tag
        size="sm"
        bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
        onClick={handleOnClick}
        overflow={'hidden'}
      >
        <Link>{rowData.filePath}</Link>
      </Tag>
    );
  };

  const handleFileName = rowData => {
    const handleOnClick = e => {
      if (!copyDebouncer.current) {
        navigator.clipboard.writeText(rowData.fileName);
        toast({
          title: 'File Name copied to clipboard',
          description: rowData.fileName,
          duration: 2000,
          isClosable: true,
        });
        copyDebouncer.current = true;
        setTimeout(() => {
          copyDebouncer.current = false;
        }, 500);
      }
    };

    return (
      <Tag
        size="sm"
        bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
        onClick={handleOnClick}
        overflow={'hidden'}
      >
        <Link>{rowData.fileName}</Link>
      </Tag>
    );
  };

  const handleSubmitterName = rowData => {
    return (
      <>
        <Tag variant={'outline'}>
          {rowData.rejection.rejected && (
            <Icon as={WarningTwoIcon} color="red" />
          )}
          {rowData.firstName} {rowData.lastName.charAt(0)}.
        </Tag>
      </>
    );
  };

  const handleNotes = rowData => {
    return (
      <Popover>
        <PopoverTrigger>
          <Center>
            <IconButton
              size="sm"
              variant="outline"
              colorScheme="teal"
              aria-label="View Notes"
              icon={<CgNotes />}
            />
          </Center>
        </PopoverTrigger>
        <Portal>
          <PopoverContent>
            <PopoverArrow />
            <PopoverHeader>Notes</PopoverHeader>
            <PopoverCloseButton />
            <PopoverBody>
              <UnorderedList>
                {rowData.notes.length !== 0 ? (
                  rowData.notes
                    .slice(0)
                    .reverse()
                    .map((e, i) => {
                      return (
                        <ListItem key={i}>
                          <Stat>
                            <StatLabel>
                              <Badge colorScheme="teal">
                                {new Date(e.timestamp).toLocaleString('en-US', {
                                  day: 'numeric', // numeric, 2-digit
                                  year: 'numeric', // numeric, 2-digit
                                  month: 'short', // numeric, 2-digit, long, short, narrow
                                  hour: 'numeric', // numeric, 2-digit
                                  minute: 'numeric', // numeric, 2-digit
                                  hour12: true,
                                })}
                              </Badge>
                            </StatLabel>
                            <StatNumber fontSize={'sm'}>{e.note}</StatNumber>
                          </Stat>
                        </ListItem>
                      );
                    })
                ) : (
                  <ListItem>No notes</ListItem>
                )}
              </UnorderedList>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  };

  const handleEdit = rowData => {
    return rowData.rejection.rejected ? (
      <Menu strategy={'fixed'}>
        <Center>
          <MenuButton size="sm" as={Button} rightIcon={<ChevronDownIcon />}>
            Actions
          </MenuButton>
        </Center>
        <MenuList>
          <MenuItem
            icon={<SearchIcon />}
            as={RouterLink}
            to={`/rejectReviewForm/${rowData._id}`}
          >
            Review Rejected Form
          </MenuItem>
          <MenuDivider />
          <MenuItem
            icon={<EditIcon />}
            as={RouterLink}
            to={`/editForm/${rowData._id}`}
          >
            Edit
          </MenuItem>
        </MenuList>
      </Menu>
    ) : (
      <Center>
        <IconButton
          size="sm"
          variant="outline"
          colorScheme="blue"
          aria-label="Edit"
          icon={<EditIcon />}
          as={RouterLink}
          to={`/editForm/${rowData._id}`}
        />
      </Center>
    );
  };

  const handleEditPFab = rowData => {
    return (
      <Center>
        <IconButton
          size="sm"
          variant="outline"
          colorScheme="blue"
          aria-label="Edit"
          icon={<EditIcon />}
          as={RouterLink}
          to={`/editApprovedForm/${rowData._id}`}
        />
      </Center>
    );
  };

  const handlePriority = rowData => {
    switch (rowData.priority) {
      case 1:
        return (
          <Tag size="sm" colorScheme="red">
            National Emergency
          </Tag>
        );
      case 2:
        return (
          <Tag size="sm" colorScheme="orange">
            High
          </Tag>
        );
      case 3:
        return (
          <Tag size="sm" colorScheme="yellow">
            Medium
          </Tag>
        );
      case 4:
        return (
          <Tag size="sm" colorScheme="purple">
            Low
          </Tag>
        );
      default:
        'Undefined';
    }
  };

  const handleRefresh = e => {
    setIsLoading(true);
    setTimeout(() => {
      getMCFForms(() => {
        setIsLoading(false);
        toast({
          title: 'Data refreshed',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      });
    }, 2000);
  };

  const subsystems = [
    'Chassis',
    'Climb',
    'Drivetrain',
    'Elevator',
    'Feeder',
    'Hopper',
    'Intake',
    'Shooter',
    'Scoring Delivery',
    'Miscellaneous',
  ];

  const subsystemFilterTemplate = options => {
    let filteredSubsystems = MCFForms.map(e => e.subsystem);
    filteredSubsystems = subsystems.filter(subsystem =>
      filteredSubsystems.includes(subsystem)
    );
    return (
      <Dropdown
        value={options.value}
        options={filteredSubsystems}
        onChange={e => options.filterCallback(e.value, options.index)}
        itemTemplate={option => {
          return <span className="image-text">{option}</span>;
        }}
        placeholder="Select a Subsystem"
        showClear
      />
    );
  };

  const mfrProcesses = ['Hurco CNC', 'Laser Cut', 'Water Jet', 'Bend'];

  const mfrProcessesFilterTemplate = options => {
    return (
      <Dropdown
        value={options.value}
        options={mfrProcesses}
        onChange={e => options.filterCallback(e.value, options.index)}
        itemTemplate={option => {
          return <span className="image-text">{option}</span>;
        }}
        placeholder="Select a machine"
        showClear
      />
    );
  };

  // const handleRerun= (rowData) => {
  //     return  <Center>
  //                 <IconButton
  //                 size="sm"
  //                 variant='outline'
  //                 colorScheme='blue'
  //                 aria-label='Edit'
  //                 icon={<RepeatIcon />}
  //                 as={RouterLink}
  //                 to={`/submitForRerun/${rowData._id}`}
  //                 />
  //             </Center>
  // }
  if (MCFForms === null) {
    return (
      <>
        <Box
          margin={'0 auto'}
          mt={'1em'}
          width={{ base: '85%', md: '66%', lg: '50%' }}
        >
          <Center>
            <Spinner mt={'2em'} size="lg" />
          </Center>
        </Box>
      </>
    );
  }
  return (
    <>
      <Box
        margin={'0 auto'}
        mt={'1em'}
        width={{ base: '100%', md: '98%', lg: '95%' }}
        p={6}
        borderWidth="1px"
        rounded="lg"
        shadow="1px 1px 3px rgba(0,0,0,0.3)"
        bgColor={'#fff'}
      >
        <Flex alignItems="center" gap="2">
          <Box p="2">
            <Heading mb={2} as="h1" size="lg">
              My Submitted Part MCFs
            </Heading>
          </Box>
          <Spacer />
          <Button
            leftIcon={<RepeatIcon />}
            isLoading={isLoading}
            loadingText="Refreshing"
            onClick={handleRefresh}
            size="lg"
            colorScheme="gray"
            textColor="teal"
            variant="solid"
          >
            Refresh
          </Button>
        </Flex>
        <Accordion
          mt={'1em'}
          onChange={index =>
            setAccordionIndex(
              // @ts-ignore
              index
            )
          }
          index={accordionIndex}
          allowMultiple
        >
          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left">
                  <Tag size={'lg'} variant="subtle" colorScheme="red">
                    <TagLeftIcon boxSize="14px" as={TimeIcon} />
                    <TagLabel>
                      {myPartCount.current.pendingMentorReview} Pending Mentor
                      Review
                    </TagLabel>
                  </Tag>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <DataTable
                sortField="priority"
                sortOrder={1}
                value={MCFForms.filter(
                  e =>
                    e.googleId === user.googleId &&
                    !e.mentorApproved &&
                    !e.approved &&
                    !e.manufactured
                )}
                showGridlines
                responsiveLayout="scroll"
                size="small"
              >
                <Column
                  field="submitter"
                  header="Submitter"
                  body={handleSubmitterName}
                />
                <Column
                  field="subsystem"
                  header="Subsystem"
                  style={{ width: '8%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.subsystem}
                    </Tag>
                  )}
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="subsystem"
                  filterElement={subsystemFilterTemplate}
                />
                <Column
                  field="filePath"
                  header="File Path"
                  style={{ width: '20%' }}
                  body={handleFilePath}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file path"
                />
                <Column
                  field="fileName"
                  header="File Name"
                  body={handleFileName}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file name"
                />
                <Column
                  field="revision"
                  header="Revision"
                  style={{ width: '5%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.revision}
                    </Tag>
                  )}
                />
                <Column
                  field="material"
                  header="Material"
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.material}
                    </Tag>
                  )}
                />
                <Column
                  field="mfrProcess"
                  header="Machine(s)"
                  style={{ width: '12%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.mfrProcess.length === 1
                        ? rowData.mfrProcess[0]
                        : rowData.mfrProcess.join(', ')}
                    </Tag>
                  )}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="mfrProcess"
                  filterElement={mfrProcessesFilterTemplate}
                />
                <Column
                  field="finish"
                  header="Finish"
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.finish}
                    </Tag>
                  )}
                />
                <Column
                  field="quantity"
                  header="Quantity"
                  style={{ width: '5%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.quantity}
                    </Tag>
                  )}
                />
                <Column
                  field="priority"
                  header="Priority"
                  style={{ width: '5%' }}
                  body={handlePriority}
                  sortable
                  sortableDisabled
                />
                <Column field="notes" header="Notes" body={handleNotes} />
                <Column header="Edit" body={handleEdit} />
              </DataTable>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left">
                  <Tag size={'lg'} variant="subtle" colorScheme="red">
                    <TagLeftIcon boxSize="14px" as={TimeIcon} />
                    <TagLabel>
                      {myPartCount.current.pendingReview} Pending Boyce Review
                    </TagLabel>
                  </Tag>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <DataTable
                sortField="priority"
                sortOrder={1}
                value={MCFForms.filter(
                  e =>
                    e.googleId === user.googleId &&
                    e.mentorApproved &&
                    !e.approved &&
                    !e.manufactured
                )}
                showGridlines
                responsiveLayout="scroll"
                size="small"
              >
                <Column
                  field="submitter"
                  header="Submitter"
                  body={handleSubmitterName}
                />
                <Column
                  field="subsystem"
                  header="Subsystem"
                  style={{ width: '8%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.subsystem}
                    </Tag>
                  )}
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="subsystem"
                  filterElement={subsystemFilterTemplate}
                />
                <Column
                  field="filePath"
                  header="File Path"
                  style={{ width: '20%' }}
                  body={handleFilePath}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file path"
                />
                <Column
                  field="fileName"
                  header="File Name"
                  body={handleFileName}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file name"
                />
                <Column
                  field="revision"
                  header="Revision"
                  style={{ width: '5%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.revision}
                    </Tag>
                  )}
                />
                <Column
                  field="material"
                  header="Material"
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.material}
                    </Tag>
                  )}
                />
                <Column
                  field="mfrProcess"
                  header="Machine(s)"
                  style={{ width: '12%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.mfrProcess.length === 1
                        ? rowData.mfrProcess[0]
                        : rowData.mfrProcess.join(', ')}
                    </Tag>
                  )}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="mfrProcess"
                  filterElement={mfrProcessesFilterTemplate}
                />
                <Column
                  field="finish"
                  header="Finish"
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.finish}
                    </Tag>
                  )}
                />
                <Column
                  field="quantity"
                  header="Quantity"
                  style={{ width: '5%' }}
                  body={rowData => (
                    <Tag
                      size="sm"
                      bgColor={rowData.rejection.rejected ? 'red' : 'gray.200'}
                    >
                      {rowData.quantity}
                    </Tag>
                  )}
                />
                <Column
                  field="priority"
                  header="Priority"
                  style={{ width: '5%' }}
                  body={handlePriority}
                  sortable
                  sortableDisabled
                />
                <Column field="notes" header="Notes" body={handleNotes} />
                <Column header="Edit" body={handleEdit} />
              </DataTable>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left">
                  <Tag size={'lg'} variant="subtle" colorScheme="orange">
                    <TagLeftIcon boxSize="14px" as={TbEyeCheck} />
                    <TagLabel>
                      {myPartCount.current.pendingFab} Pending Fabrication
                    </TagLabel>
                  </Tag>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <DataTable
                sortField="priority"
                sortOrder={1}
                value={MCFForms.filter(
                  e =>
                    e.googleId === user.googleId &&
                    e.mentorApproved &&
                    e.approved &&
                    !e.manufactured
                )}
                showGridlines
                responsiveLayout="scroll"
                size="small"
              >
                <Column
                  field="submitter"
                  header="Submitter"
                  body={handleSubmitterName}
                />
                <Column
                  field="subsystem"
                  header="Subsystem"
                  style={{ width: '8%' }}
                  body={rowData => <Tag size="sm">{rowData.subsystem}</Tag>}
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="subsystem"
                  filterElement={subsystemFilterTemplate}
                />
                <Column
                  field="filePath"
                  header="File Path"
                  style={{ width: '20%' }}
                  body={handleFilePath}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file path"
                />
                <Column
                  field="fileName"
                  header="File Name"
                  body={handleFileName}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file name"
                />
                <Column
                  field="revision"
                  header="Revision"
                  style={{ width: '5%' }}
                  body={rowData => <Tag size="sm">{rowData.revision}</Tag>}
                />
                <Column
                  field="material"
                  header="Material"
                  body={rowData => <Tag size="sm">{rowData.material}</Tag>}
                />
                <Column
                  field="mfrProcess"
                  header="Machine(s)"
                  style={{ width: '12%' }}
                  body={rowData => (
                    <Tag size="sm">
                      {rowData.mfrProcess.length === 1
                        ? rowData.mfrProcess[0]
                        : rowData.mfrProcess.join(', ')}
                    </Tag>
                  )}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="mfrProcess"
                  filterElement={mfrProcessesFilterTemplate}
                />
                <Column
                  field="finish"
                  header="Finish"
                  body={rowData => <Tag size="sm">{rowData.finish}</Tag>}
                />
                <Column
                  field="quantity"
                  header="Quantity"
                  style={{ width: '5%' }}
                  body={rowData => <Tag size="sm">{rowData.quantity}</Tag>}
                />
                <Column
                  field="priority"
                  header="Priority"
                  style={{ width: '5%' }}
                  body={handlePriority}
                  sortable
                  sortableDisabled
                />
                <Column field="notes" header="Notes" body={handleNotes} />
                <Column header="Edit" body={handleEditPFab} />
              </DataTable>
            </AccordionPanel>
          </AccordionItem>
          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left">
                  <Tag size={'lg'} variant="subtle" colorScheme="green">
                    <TagLeftIcon boxSize="14px" as={BsCheckCircle} />
                    <TagLabel>
                      {myPartCount.current.fabCompleted} Fabrication Completed
                    </TagLabel>
                  </Tag>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <DataTable
                value={MCFForms.filter(
                  e => e.googleId === user.googleId && e.manufactured
                )}
                showGridlines
                responsiveLayout="scroll"
                size="small"
              >
                <Column
                  field="submitter"
                  header="Submitter"
                  body={handleSubmitterName}
                />
                <Column
                  field="subsystem"
                  header="Subsystem"
                  style={{ width: '8%' }}
                  body={rowData => <Tag size="sm">{rowData.subsystem}</Tag>}
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="subsystem"
                  filterElement={subsystemFilterTemplate}
                />
                <Column
                  field="filePath"
                  header="File Path"
                  style={{ width: '20%' }}
                  body={handleFilePath}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file path"
                />
                <Column
                  field="fileName"
                  header="File Name"
                  body={handleFileName}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterPlaceholder="Search by file name"
                />
                <Column
                  field="revision"
                  header="Revision"
                  style={{ width: '5%' }}
                  body={rowData => <Tag size="sm">{rowData.revision}</Tag>}
                />
                <Column
                  field="material"
                  header="Material"
                  body={rowData => <Tag size="sm">{rowData.material}</Tag>}
                />
                <Column
                  field="mfrProcess"
                  header="Machine(s)"
                  style={{ width: '12%' }}
                  body={rowData => (
                    <Tag size="sm">
                      {rowData.mfrProcess.length === 1
                        ? rowData.mfrProcess[0]
                        : rowData.mfrProcess.join(', ')}
                    </Tag>
                  )}
                  filterMatchMode="contains"
                  showFilterMatchModes={false}
                  showFilterMenuOptions={false}
                  filter
                  filterField="mfrProcess"
                  filterElement={mfrProcessesFilterTemplate}
                />
                <Column
                  field="finish"
                  header="Finish"
                  body={rowData => <Tag size="sm">{rowData.finish}</Tag>}
                />
                <Column
                  field="quantity"
                  header="Quantity"
                  style={{ width: '5%' }}
                  body={rowData => <Tag size="sm">{rowData.quantity}</Tag>}
                />
                <Column
                  field="priority"
                  header="Priority"
                  style={{ width: '5%' }}
                  body={handlePriority}
                  sortable
                  sortableDisabled
                />
                <Column field="notes" header="Notes" body={handleNotes} />
              </DataTable>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>
    </>
  );
}
